import { css } from "@linaria/core";
import cx from "classnames";
import { DateTime } from "luxon";
import type { SetStateAction } from "react";
import React from "react";
import t from "../../../../../../i18n";
import Text from "@ui/Text";
import ReportingChartMenu from "../../../ReportingChartMenu";
import {
  usePeriodComparison,
  usePeriodComparisonDuration,
  usePeriodComparisonOptions,
} from "../../../usePeriodComparison";
import { useReportingChartPeriod } from "../../../useReportingChartPeriod";
import type { PerformanceReporting } from "../../../../../../api/useReporting";

const PeriodComparisonContainer = css`
  align-items: baseline;
  gap: 1ch;
  display: flex;
`;

export function PeriodComparisonSelector({
  reporting,
  className,
}: {
  reporting: PerformanceReporting;
  className?: string;
}) {
  const periodComparisonOptions = usePeriodComparisonOptions(reporting.range);
  const [selectedPeriodComparison, setSelectedPeriodComparison] = usePeriodComparison(
    reporting.range,
  );

  const periodComparisonDuration = usePeriodComparisonDuration(reporting.range);

  /**
   * Display, in human readable format, the selected interval duration.
   */
  const comparaisonPeriodRange = React.useMemo(() => {
    if (!periodComparisonDuration) return null;
    const { years, months } = periodComparisonDuration;
    return t("reporting.title.failures.date.range", Math.floor(years), Math.round(months));
  }, [periodComparisonDuration]);

  /**
   * Handle period from previous chart navigator selected date range.
   * Update period comparison accordingly.
   */
  const [chartPeriod, setChartPeriod] = useReportingChartPeriod(reporting, reporting.range);

  const [selectedMinMonth, selectedMaxMonth] = React.useMemo(() => {
    const min = DateTime.fromMillis(chartPeriod.from).toFormat("yyyy-MM");
    const max = DateTime.fromMillis(chartPeriod.to).toFormat("yyyy-MM");
    return [min, max];
  }, [chartPeriod]);

  React.useEffect(() => {
    if (selectedMinMonth && selectedMaxMonth) {
      setSelectedPeriodComparison(() => ({ min: selectedMinMonth, max: selectedMaxMonth }));
    }
  }, [selectedMinMonth, selectedMaxMonth, setSelectedPeriodComparison]);

  /**
   * Define chronological available options.
   * Max cannot be inferior to min && min cannot be superior to max.
   */
  const minPeriodComparisonOptions = React.useMemo(() => {
    if (!selectedPeriodComparison || selectedPeriodComparison.max === null)
      return periodComparisonOptions;

    const max = DateTime.fromISO(selectedPeriodComparison.max);
    return periodComparisonOptions.filter(({ value }) => DateTime.fromISO(value) <= max);
  }, [selectedPeriodComparison, periodComparisonOptions]);

  const maxPeriodComparisonOptions = React.useMemo(() => {
    if (!selectedPeriodComparison || selectedPeriodComparison.min === null)
      return periodComparisonOptions;

    const min = DateTime.fromISO(selectedPeriodComparison.min as string);
    return periodComparisonOptions.filter(({ value }) => DateTime.fromISO(value) >= min);
  }, [selectedPeriodComparison, periodComparisonOptions]);

  /**
   * Update chart period & period comparison when min is updated from selector.
   */
  const setMinMonth = React.useCallback(
    (value: SetStateAction<string>) => {
      const from = DateTime.fromISO(value as string).toMillis();

      const to = selectedPeriodComparison?.max
        ? DateTime.fromISO(selectedPeriodComparison.max).toMillis()
        : null;

      setSelectedPeriodComparison(state => ({ ...state, min: value as string }));
      setChartPeriod(period => ({
        from,
        to: to ?? period.to,
      }));
    },
    [selectedPeriodComparison?.max, setChartPeriod, setSelectedPeriodComparison],
  );

  /**
   * Update chart period & period comparison when max is updated from selector.
   */
  const setMaxMonth = React.useCallback(
    (value: SetStateAction<string>) => {
      const from = selectedPeriodComparison?.min
        ? DateTime.fromISO(selectedPeriodComparison.min).toMillis()
        : null;

      const to = DateTime.fromISO(value as string)
        .endOf("month")
        .toMillis();

      setSelectedPeriodComparison(state => ({ ...state, max: value as string }));
      setChartPeriod(period => ({
        from: from ?? period.from,
        to,
      }));
    },
    [selectedPeriodComparison?.min, setChartPeriod, setSelectedPeriodComparison],
  );

  return selectedPeriodComparison?.min && selectedPeriodComparison?.max ? (
    <div className={cx(PeriodComparisonContainer, className)}>
      <Text type="body2SemiBold">{t("commons.date_picker.from")}</Text>
      <ReportingChartMenu
        options={minPeriodComparisonOptions}
        currentValue={selectedMinMonth}
        setCurrentValue={setMinMonth}
      />
      <Text type="body2SemiBold">{t("commons.date_picker.to")}</Text>
      <ReportingChartMenu
        options={maxPeriodComparisonOptions}
        currentValue={selectedMaxMonth}
        setCurrentValue={setMaxMonth}
      />
      {comparaisonPeriodRange && comparaisonPeriodRange.length > 2 && (
        <Text type="body2SemiBold">{comparaisonPeriodRange}</Text>
      )}
    </div>
  ) : null;
}

export default React.memo(PeriodComparisonSelector);
